import { Controller } from "@hotwired/stimulus"

/**
 * Support inbox sidebar filters (CONEK-4296).
 *
 * The sidebar lives OUTSIDE the `ticket-content` Turbo frame, so every filter
 * link swaps only that frame (`data-turbo-frame="ticket-content"` +
 * `data-turbo-action="advance"`). The sidebar is never remounted, which keeps
 * its scroll position intact. This controller's only job is to keep the sidebar
 * in sync with the URL after each frame navigation:
 *
 *   - rewrite each toggle link's href so the next click adds/removes its value
 *   - apply the active / inactive class set to every link
 *   - show/hide the Status section (list view only)
 *
 * Everything is derived from `window.location` (the single source of truth that
 * Turbo "advance" keeps current), so there is no client/server drift. Native
 * anchors perform the navigation; we never call preventDefault or touch history.
 *
 * Behaviour:
 *   - INBOXES (kind="inbox") is the main selector: choosing one clears every
 *     TYPE / SENTIMENT / STATUS toggle (view mode is kept).
 *   - TYPE / SENTIMENT / STATUS (kind="toggle") are additive multi-select. The
 *     Status section toggles `business_status[]` (CONEK-6786): one agent-facing
 *     status per link, expanded server-side to its (status, reason) tuple.
 *   - kind="single" is a replace-or-clear filter (priority-style).
 *   - kind="view" sets the sticky Board/List mode.
 */
export default class extends Controller {
  static targets = ["statusSection"]
  static values = {
    baseUrl: String,
    viewMode: String,
  }

  // Multi-select toggle keys. Values may arrive as `key` or `key[]`.
  // `business_status` (CONEK-6786) is the agent-facing status filter the
  // sidebar renders; `status` / `waiting_reason` stay for saved views and
  // deep links that predate it.
  static TOGGLE_KEYS = [
    "ticket_type",
    "sentiment",
    "status",
    "waiting_reason",
    "business_status",
  ]
  // Inbox boolean flags, in the precedence order the server uses.
  static INBOX_FLAGS = [
    "my_tickets",
    "unassigned",
    "watching",
    "sla_breached",
    "snoozed",
  ]

  connect() {
    this._sync = this._sync.bind(this)
    this._sync()
    // Re-sync after the ticket-content frame finishes a filter navigation and
    // after full Turbo Drive page loads.
    document.addEventListener("turbo:frame-load", this._sync)
    document.addEventListener("turbo:load", this._sync)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this._sync)
    document.removeEventListener("turbo:load", this._sync)
  }

  _sync() {
    // Selecting a ticket advances the URL to /support/tickets/:id (no filter
    // query). Re-deriving state from that URL would wipe the active filters, so
    // we only sync while on the index path itself and otherwise keep whatever
    // the sidebar already shows.
    const indexPath = new URL(this.baseUrlValue, window.location.origin)
      .pathname
    if (window.location.pathname !== indexPath) return

    const params = this._currentParams()
    // When a saved view is applied the URL only carries `saved_view_id`; the
    // real filters were merged server-side. Without the frame's published
    // effective filters (CONEK-8035) deriving from the URL would wrongly clear
    // them, so we trust the server render and leave the sidebar untouched.
    if (!params) return
    this.element.querySelectorAll("[data-inbox-filter]").forEach((link) => {
      link.setAttribute("href", this._hrefFor(link, params))
      this._applyState(link, this._isActive(link, params))
    })
    this._syncStatusSections(params)
    this._syncSavedViews(params)
  }

  // The filters the page shows. With a saved view applied they are the ones the
  // server resolved it to, published in the ticket-content frame as
  // `data-support-effective-filters` (CONEK-8035), plus the view's id — so a
  // toggle click refines the view instead of replacing it with one filter.
  _currentParams() {
    const params = new URLSearchParams(window.location.search)
    const viewId = params.get("saved_view_id")
    if (!viewId) return params

    const published = document.querySelector("[data-support-effective-filters]")
    if (!published) return null
    const effective = new URLSearchParams(
      published.dataset.supportEffectiveFilters || "",
    )
    effective.set("saved_view_id", viewId)
    return effective
  }

  // A saved view is only "active" while its id is in the URL. Once the user
  // changes any filter the saved_view_id drops out of the URL (and the early
  // return above no longer fires), so clear the stale highlight here. When a
  // saved view IS applied (saved_view_id present) we early-return and leave the
  // server-rendered highlight untouched.
  _syncSavedViews(params) {
    const activeId = params.get("saved_view_id") || ""
    this.element.querySelectorAll("[data-saved-view-link]").forEach((link) => {
      this._applyState(link, link.dataset.savedViewLink === activeId)
    })
  }

  // ── URL building ──────────────────────────────────────────────────────────

  _hrefFor(link, current) {
    const kind = link.dataset.inboxFilter
    const params = new URLSearchParams(current.toString())
    // Any filter change resets pagination and drops the saved-view context.
    params.delete("page")
    params.delete("saved_view_id")
    // The currently displayed mode: the URL param wins, otherwise the mode the
    // server rendered for THIS page. We always re-encode it into the generated
    // URL (see _withViewMode) so navigation reflects what the user is looking at
    // and never falls back to a stale/cross-tab cookie value.
    const viewMode = params.get("view_mode") || this.viewModeValue

    if (kind === "view") {
      params.set("view_mode", link.dataset.filterViewMode || "list")
      return this._url(params)
    }

    if (kind === "inbox") {
      // INBOXES is the main selector: reset every other filter, keep the mode.
      const next = new URLSearchParams()
      const inbox = link.dataset.filterInbox
      if (inbox && inbox !== "all") next.set(inbox, "true")
      return this._url(this._withViewMode(next, viewMode))
    }

    if (kind === "single") {
      const key = link.dataset.filterKey
      const value = link.dataset.filterValue
      if (!value || params.get(key) === value) {
        params.delete(key)
      } else {
        params.set(key, value)
      }
      return this._url(this._withViewMode(params, viewMode))
    }

    // kind === "toggle"
    const key = link.dataset.filterKey
    const value = link.dataset.filterValue
    this._toggleMulti(params, key, value)

    return this._url(this._withViewMode(params, viewMode))
  }

  // Always carry the displayed view mode so the server renders it from the URL
  // rather than the cookie (which can drift across tabs/sessions).
  _withViewMode(params, viewMode) {
    params.set("view_mode", viewMode === "board" ? "board" : "list")
    return params
  }

  _url(params) {
    const query = params.toString()
    return query ? `${this.baseUrlValue}?${query}` : this.baseUrlValue
  }

  // ── Active state ──────────────────────────────────────────────────────────

  _isActive(link, params) {
    const kind = link.dataset.inboxFilter
    const viewMode = params.get("view_mode") || this.viewModeValue

    if (kind === "view") {
      return (link.dataset.filterViewMode || "list") === viewMode
    }
    if (kind === "inbox") {
      return this._activeInbox(params) === (link.dataset.filterInbox || "all")
    }
    if (kind === "single") {
      const value = link.dataset.filterValue
      if (!value) return !params.get(link.dataset.filterKey)
      return params.get(link.dataset.filterKey) === value
    }
    // toggle
    return this._getMulti(params, link.dataset.filterKey).includes(
      link.dataset.filterValue,
    )
  }

  // Mirror the server's active_inbox precedence (see _sidebar.html.erb).
  _activeInbox(params) {
    if (params.get("saved_view_id")) return "saved_view"
    for (const flag of this.constructor.INBOX_FLAGS) {
      if (params.get(flag)) return flag
    }
    const hasFilter = [...params.keys()].some(
      (k) =>
        !["view_mode", "page", "per_page"].includes(k.replace(/\[\]$/, "")),
    )
    return hasFilter ? null : "all"
  }

  _applyState(link, active) {
    const on = (link.dataset.activeClass || "").split(/\s+/).filter(Boolean)
    const off = (link.dataset.inactiveClass || "").split(/\s+/).filter(Boolean)
    link.classList.remove(...on, ...off)
    link.classList.add(...(active ? on : off))
  }

  _syncStatusSections(params) {
    // STATUS is meaningless in the board view (statuses are the columns), so
    // the section is list-only.
    const viewMode = params.get("view_mode") || this.viewModeValue
    const isList = viewMode !== "board"

    if (this.hasStatusSectionTarget) {
      this.statusSectionTarget.classList.toggle("hidden", !isList)
    }
  }

  // ── Multi-value param helpers (support both `key` and `key[]` forms) ────────

  _getMulti(params, key) {
    return [...params.getAll(`${key}[]`), ...params.getAll(key)].filter(Boolean)
  }

  _setMulti(params, key, values) {
    params.delete(key)
    params.delete(`${key}[]`)
    values.forEach((v) => params.append(`${key}[]`, v))
  }

  _toggleMulti(params, key, value) {
    const set = new Set(this._getMulti(params, key))
    if (set.has(value)) {
      set.delete(value)
    } else {
      set.add(value)
    }
    this._setMulti(params, key, [...set])
  }
}
